// Abnahmen eines Tickets: wer hat wann die erledigte Arbeit quittiert?
// Die Unterschrift selbst liegt als Bild im Ticket-Anhang-Speicher.
// Läuft als Tab im „Einsatz“-Widget: Titel und Karte liefert das Widget.
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Signature, SpinnerGap, WarningCircle, X } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import AbnahmeUnterschrift from './AbnahmeUnterschrift';
import { AppKachel } from '../ui/AppIconKnopf';

interface Props {
  ticketId: string;
  userProfile?: { id?: string } | null;
  /** Anzahl Abnahmen (Zähler im Einsatz-Tab) */
  onAnzahl?: (n: number) => void;
}

interface Abnahme {
  id: string;
  name: string;
  funktion: string;
  bemerkung: string | null;
  unterschrift_path: string;
  created_at: string;
}

const FUNKTION: Record<string, string> = { mieter: 'Mieter', eigentuemer: 'Eigentümer', sonstige: 'Sonstige' };

const TicketAbnahmen: React.FC<Props> = ({ ticketId, userProfile, onAnzahl }) => {
  const [abnahmen, setAbnahmen] = useState<Abnahme[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [laedt, setLaedt] = useState(true);
  const [neu, setNeu] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    const { data } = await supabase
      .from('ticket_abnahmen')
      .select('id, name, funktion, bemerkung, unterschrift_path, created_at')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: false });
    const liste = (data || []) as Abnahme[];
    setAbnahmen(liste);
    const paare = await Promise.all(liste.map(async (a) => {
      const { data: u } = await supabase.storage.from('ticket-anhaenge').createSignedUrl(a.unterschrift_path, 3600);
      return [a.id, u?.signedUrl || ''] as const;
    }));
    setUrls(Object.fromEntries(paare));
    setLaedt(false);
  }, [ticketId]);

  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { onAnzahl?.(abnahmen.length); }, [abnahmen.length, onAnzahl]);

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {fehler && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '9px 12px', borderRadius: 12, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 12.5, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" />
          <span className="flex-1">{fehler}</span>
          <button type="button" onClick={() => setFehler(null)} aria-label="Hinweis schließen" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}>
            <X size={13} weight="bold" />
          </button>
        </div>
      )}

      {neu && (
        <AbnahmeUnterschrift
          ticketId={ticketId}
          meineId={userProfile?.id || ''}
          onFertig={() => { setNeu(false); setFehler(null); void laden(); }}
          onAbbruch={() => setNeu(false)}
          onFehler={setFehler}
        />
      )}

      {laedt ? (
        <div className="flex justify-center" style={{ padding: '16px 0', color: 'var(--ink3)' }}><SpinnerGap size={16} className="animate-spin" /></div>
      ) : abnahmen.length === 0 ? (
        !neu && <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Noch keine Abnahme unterschrieben.</p>
      ) : (
        <div className="flex flex-col">
          {abnahmen.map((a) => (
            <div key={a.id} className="flex items-center" style={{ gap: 10, padding: '8px 2px', borderBottom: '1px solid var(--line)', fontSize: 12.5 }}>
              {urls[a.id] && (
                // Unterschrift bleibt auf hellem „Papier“ — die Striche sind dunkel und wären im Dunkelmodus sonst unsichtbar.
                <img src={urls[a.id]} alt="Unterschrift" className="shrink-0 object-contain"
                  style={{ width: 96, height: 44, borderRadius: 9, background: '#fff', boxShadow: 'inset 0 0 0 1px var(--line)' }} />
              )}
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
                <span className="flex min-w-0 items-center" style={{ gap: 6 }}>
                  <CheckCircle size={14} weight="fill" className="shrink-0" style={{ color: 'var(--ok)' }} />
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{a.name}</span>
                  <span className="shrink-0" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>({FUNKTION[a.funktion] || a.funktion})</span>
                </span>
                {a.bemerkung && <span className="break-words" style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{a.bemerkung}</span>}
              </span>
              <span className="shrink-0 tabular-nums" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>
                {new Date(a.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
        </div>
      )}

      {!neu && !laedt && (
        <AppKachel icon={Signature} farbe="lila" titel={abnahmen.length === 0 ? 'Unterschrift einholen' : 'Weitere Unterschrift'}
          unter="Abnahme vor Ort" tip="Unterschrift einholen – Abnahme vor Ort unterschreiben lassen"
          onClick={() => setNeu(true)} />
      )}
    </div>
  );
};

export default TicketAbnahmen;
